import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Tesseract from 'tesseract.js';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './ClienteRegistro.css';

// Fix para iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ClienteRegistro = ({ accessCode, onClose }) => {
  const [formData, setFormData] = useState({
    cedula: '',
    nombreCompleto: '',
    direccionPrincipal: '',
    coordenadasGPS: { latitud: null, longitud: null, precision: null },
    numeroCajaNAT: 'FN-',
    telefonoWhatsApp: '',
    nombreSSIDWifi: 'FN-',
    contrasenaWifi: '',
    imagenCedula: null,
    datosExtraIdosIA: null
  });

  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [tempCoords, setTempCoords] = useState({ latitud: null, longitud: null, precision: null });
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [cedulaDetectada, setCedulaDetectada] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Limpiar stream de cámara al desmontar
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convertir nombre completo a mayúsculas
    if (name === 'nombreCompleto') {
      setFormData({
        ...formData,
        [name]: value.toUpperCase()
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const obtenerUbicacionGPS = () => {
    if (!navigator.geolocation) {
      alert('Tu navegador no soporta geolocalización');
      return;
    }

    setGpsLoading(true);
    setGpsStatus('Obteniendo ubicación GPS... Por favor salga afuera para mejor precisión (10-20 metros)');

    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        
        // Guardar coordenadas temporales
        setTempCoords({
          latitud: latitude,
          longitud: longitude,
          precision: accuracy
        });

        setGpsLoading(false);
        setShowMapModal(true); // Mostrar modal de mapa
        
        if (accuracy <= 20) {
          setGpsStatus(`✓ Ubicación obtenida con precisión de ${accuracy.toFixed(1)} metros`);
        } else {
          setGpsStatus(`⚠ Ubicación obtenida con precisión de ${accuracy.toFixed(1)} metros (recomendado: salir afuera para mejor señal)`);
        }
      },
      (error) => {
        setGpsLoading(false);
        let errorMsg = 'Error al obtener ubicación GPS';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Permiso de ubicación denegado. Por favor habilita el GPS en tu navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Ubicación no disponible. Por favor sal afuera para mejor señal.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Tiempo de espera agotado. Intenta de nuevo.';
            break;
          default:
            errorMsg = 'Error desconocido al obtener ubicación.';
        }
        
        setGpsStatus(errorMsg);
        alert(errorMsg);
      },
      options
    );
  };

  const iniciarCamara = async () => {
    try {
      setShowCamera(true);
      
      // Primero intentar con cámara trasera
      let constraints = {
        video: { 
          facingMode: { exact: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        // Si falla con exact, intentar sin exact
        console.log('Intentando sin modo exact...');
        constraints = {
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }
      
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Asegurarse que el video se reproduzca
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => {
            console.error('Error al reproducir video:', e);
          });
          
          // Iniciar detección automática de cédula
          iniciarDeteccionAutomatica();
        };
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
      setShowCamera(false);
      alert('No se pudo acceder a la cámara. Por favor verifica los permisos en la configuración de tu navegador.');
    }
  };

  const iniciarDeteccionAutomatica = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    detectionIntervalRef.current = setInterval(() => {
      if (videoRef.current && canvasRef.current && !ocrProcessing) {
        const detectada = detectarCedulaCentrada();
        
        if (detectada && !cedulaDetectada) {
          // Cédula detectada por primera vez
          setCedulaDetectada(true);
          iniciarCountdown();
        } else if (!detectada && cedulaDetectada) {
          // Cédula ya no está centrada
          setCedulaDetectada(false);
          detenerCountdown();
        }
      }
    }, 300); // Revisar cada 300ms
  };

  const detectarCedulaCentrada = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return false;

    const context = canvas.getContext('2d');
    
    // Configurar canvas con el tamaño del video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Calcular área del cuadro guía (proporción 85.60 x 53.98)
    const aspectRatio = 53.98 / 85.60; // altura/ancho
    const guideWidth = canvas.width * 0.8;
    const guideHeight = guideWidth * aspectRatio;
    const guideX = (canvas.width - guideWidth) / 2;
    const guideY = (canvas.height - guideHeight) / 2;
    
    // Dibujar frame actual
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Obtener datos de la región del cuadro
    const imageData = context.getImageData(guideX, guideY, guideWidth, guideHeight);
    const data = imageData.data;
    
    // Analizar contraste y bordes
    let totalContraste = 0;
    const sampleRate = 20; // Tomar muestras cada 20 píxeles para velocidad
    
    for (let i = 0; i < data.length; i += 4 * sampleRate) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Calcular brillo
      const brightness = (r + g + b) / 3;
      
      // Comparar con píxel vecino para detectar bordes
      if (i + 4 * sampleRate < data.length) {
        const nextR = data[i + 4 * sampleRate];
        const nextG = data[i + 1 + 4 * sampleRate];
        const nextB = data[i + 2 + 4 * sampleRate];
        const nextBrightness = (nextR + nextG + nextB) / 3;
        
        totalContraste += Math.abs(brightness - nextBrightness);
      }
    }
    
    // Si hay suficiente contraste, hay algo (probablemente una cédula)
    const contrastePromedio = totalContraste / (data.length / (4 * sampleRate));
    const umbralContraste = 15; // Ajustable según pruebas
    
    console.log(`Contraste detectado: ${contrastePromedio.toFixed(2)}`);
    
    return contrastePromedio > umbralContraste;
  };

  const iniciarCountdown = () => {
    setCountdown(3);
    
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Capturar automáticamente
          clearInterval(countdownIntervalRef.current);
          capturarFoto();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const detenerCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setCountdown(0);
  };

  const procesarCedulaConIA = async (imagenBase64) => {
    setOcrProcessing(true);
    setOcrProgress(10); // Iniciar con 10%

    try {
      // Configurar para procesar más rápido
      setOcrProgress(20);
      
      const result = await Tesseract.recognize(
        imagenBase64,
        'spa',
        {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              // Ajustar progreso de 20% a 90%
              const progress = 20 + Math.round(m.progress * 70);
              setOcrProgress(progress);
            }
          },
          // Optimizaciones para velocidad
          tessedit_pageseg_mode: Tesseract.PSM.AUTO,
        }
      );

      setOcrProgress(95);

      const texto = result.data.text;
      console.log('Texto extraído:', texto);

      // Extraer número de cédula (formato: 096-0007234-3 o 09600072343)
      const regexCedula = /(\d{3})-?(\d{7})-?(\d{1})/;
      const matchCedula = texto.match(regexCedula);
      
      let cedula = '';
      if (matchCedula) {
        cedula = `${matchCedula[1]}-${matchCedula[2]}-${matchCedula[3]}`;
      }

      // Extraer nombre completo - En cédulas dominicanas:
      // Línea 1: NOMBRE(S)
      // Línea 2: APELLIDOS
      let nombreCompleto = '';
      const lineas = texto.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      
      console.log('=== ANÁLISIS DE LÍNEAS PARA NOMBRES ===');
      
      let candidatos = [];
      
      // Palabras clave a excluir
      const palabrasClave = [
        'REPUBLICA', 'DOMINICANA', 'JUNTA', 'CENTRAL', 'ELECTORAL',
        'CEDULA', 'IDENTIDAD', 'SANGRE', 'SEXO', 'OCUPACION',
        'NACIONALIDAD', 'LUGAR', 'FECHA', 'EXPIRACION', 'NACIMIENTO',
        'ESTADO', 'CIVIL', 'DOCUMENTO', 'IDENTIFICACION'
      ];
      
      for (let i = 0; i < lineas.length; i++) {
        const linea = lineas[i];
        
        // Verificar que no contenga palabras clave
        const tieneClaves = palabrasClave.some(palabra => linea.includes(palabra));
        
        // Verificar que no tenga números (excepto si hay muy pocos)
        const numeros = linea.match(/\d/g);
        const tieneDemasiadosNumeros = numeros && numeros.length > 2;
        
        // Verificar que sea mayormente mayúsculas (al menos 70%)
        const mayusculas = linea.match(/[A-ZÁÉÍÓÚÑ]/g);
        const porcentajeMayusculas = mayusculas ? (mayusculas.length / linea.replace(/\s/g, '').length) : 0;
        
        // Verificar que tenga al menos 2 palabras
        const palabras = linea.split(/\s+/).filter(p => p.length > 1);
        
        console.log(`Línea ${i}: "${linea}"`);
        console.log(`  - Tiene claves: ${tieneClaves}`);
        console.log(`  - Números: ${numeros ? numeros.length : 0}`);
        console.log(`  - % Mayúsculas: ${(porcentajeMayusculas * 100).toFixed(1)}%`);
        console.log(`  - Palabras: ${palabras.length}`);
        
        if (!tieneClaves && 
            !tieneDemasiadosNumeros && 
            porcentajeMayusculas > 0.7 &&
            palabras.length >= 2 &&
            linea.length >= 6) {
          
          candidatos.push({
            indice: i,
            texto: linea,
            palabras: palabras.length
          });
          console.log(`  ✓ CANDIDATO para nombre`);
        }
      }
      
      console.log(`\nTotal candidatos encontrados: ${candidatos.length}`);
      
      // Buscar 2 candidatos consecutivos o cercanos
      if (candidatos.length >= 2) {
        // Intentar encontrar los dos primeros candidatos que estén cerca
        for (let i = 0; i < candidatos.length - 1; i++) {
          const diferencia = candidatos[i + 1].indice - candidatos[i].indice;
          if (diferencia <= 3) { // Permitir hasta 2 líneas de separación
            nombreCompleto = `${candidatos[i].texto} ${candidatos[i + 1].texto}`;
            console.log(`✓ Nombre combinado: "${nombreCompleto}"`);
            break;
          }
        }
      }
      
      // Si no encontramos 2 consecutivos, usar el primero si existe
      if (!nombreCompleto && candidatos.length >= 1) {
        nombreCompleto = candidatos[0].texto;
        console.log(`✓ Usando primer candidato: "${nombreCompleto}"`);
      }

      // Limpiar espacios extras
      nombreCompleto = nombreCompleto.replace(/\s+/g, ' ').trim();

      setOcrProgress(100);

      // Actualizar formulario con los datos extraídos
      setFormData(prev => ({
        ...prev,
        cedula: cedula || prev.cedula,
        nombreCompleto: nombreCompleto || prev.nombreCompleto,
        datosExtraIdosIA: {
          procesado: true,
          fecha: new Date().toISOString(),
          textoCompleto: texto,
          cedulaDetectada: cedula,
          nombreDetectado: nombreCompleto,
          confianza: result.data.confidence
        }
      }));

      if (cedula && nombreCompleto) {
        alert(`✓ Datos extraídos exitosamente:\n\nCédula: ${cedula}\n\nNombre: ${nombreCompleto}\n\nVerifica que sean correctos.`);
      } else {
        alert('⚠ Algunos datos no se pudieron extraer. Por favor completa manualmente:\n' + 
              (cedula ? '' : '- Cédula\n') + 
              (nombreCompleto ? '' : '- Nombre completo'));
      }

    } catch (error) {
      console.error('Error en OCR:', error);
      alert('Error al procesar la imagen. Por favor ingresa los datos manualmente.');
      
      setFormData(prev => ({
        ...prev,
        datosExtraIdosIA: {
          procesado: false,
          error: error.message,
          fecha: new Date().toISOString()
        }
      }));
    } finally {
      setOcrProcessing(false);
      setOcrProgress(0);
    }
  };

  const capturarFoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imagenBase64 = canvas.toDataURL('image/jpeg', 0.8);
      
      setFormData({
        ...formData,
        imagenCedula: imagenBase64
      });

      // Detener detección automática
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      setCedulaDetectada(false);
      setCountdown(0);

      // Procesar con IA
      procesarCedulaConIA(imagenBase64);

      // Detener cámara
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      setShowCamera(false);
    }
  };

  const cancelarCamara = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setCedulaDetectada(false);
    setCountdown(0);
    setShowCamera(false);
  };

  // Componente para manejar eventos del mapa
  const DraggableMarker = () => {
    const [position, setPosition] = useState([
      tempCoords.latitud, 
      tempCoords.longitud
    ]);

    const map = useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setPosition([lat, lng]);
        setTempCoords(prev => ({
          ...prev,
          latitud: lat,
          longitud: lng
        }));
      },
    });

    const markerRef = useRef(null);

    const eventHandlers = {
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const { lat, lng } = marker.getLatLng();
          setPosition([lat, lng]);
          setTempCoords(prev => ({
            ...prev,
            latitud: lat,
            longitud: lng
          }));
        }
      },
    };

    return (
      <Marker
        draggable={true}
        eventHandlers={eventHandlers}
        position={position}
        ref={markerRef}
      />
    );
  };

  const aceptarUbicacion = () => {
    setFormData({
      ...formData,
      coordenadasGPS: {
        latitud: tempCoords.latitud,
        longitud: tempCoords.longitud,
        precision: tempCoords.precision
      },
      direccionPrincipal: formData.direccionPrincipal || `Lat: ${tempCoords.latitud.toFixed(6)}, Lon: ${tempCoords.longitud.toFixed(6)}`
    });
    setShowMapModal(false);
  };

  const cancelarMapa = () => {
    setShowMapModal(false);
    setTempCoords({ latitud: null, longitud: null, precision: null });
  };

  const validarFormulario = () => {
    if (!formData.cedula.trim()) {
      alert('La cédula es obligatoria');
      return false;
    }
    if (!formData.nombreCompleto.trim()) {
      alert('El nombre completo es obligatorio');
      return false;
    }
    if (!formData.direccionPrincipal.trim()) {
      alert('La dirección principal es obligatoria');
      return false;
    }
    if (!formData.coordenadasGPS.latitud || !formData.coordenadasGPS.longitud) {
      alert('Debes obtener la ubicación GPS');
      return false;
    }
    if (!formData.numeroCajaNAT.trim() || !formData.numeroCajaNAT.startsWith('FN-')) {
      alert('El número de caja NAT debe comenzar con FN-');
      return false;
    }
    if (!formData.telefonoWhatsApp.trim()) {
      alert('El teléfono de WhatsApp es obligatorio');
      return false;
    }
    if (!/^(809|829|849)/.test(formData.telefonoWhatsApp)) {
      alert('El teléfono debe comenzar con 809, 829 o 849');
      return false;
    }
    if (!formData.nombreSSIDWifi.trim() || !formData.nombreSSIDWifi.startsWith('FN-')) {
      alert('El nombre del SSID WiFi debe comenzar con FN-');
      return false;
    }
    if (!formData.contrasenaWifi.trim() || formData.contrasenaWifi.length < 8) {
      alert('La contraseña WiFi debe tener al menos 8 caracteres');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post('/api/clientes', formData, {
        headers: {
          'x-access-code': accessCode
        }
      });

      if (response.data.success) {
        alert('Cliente registrado exitosamente');
        onClose();
      }
    } catch (error) {
      console.error('Error registrando cliente:', error);
      alert('Error al registrar cliente: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="cliente-registro-overlay" onClick={onClose}>
        <div className="cliente-registro-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📋 Registrar Nuevo Cliente</h2>
            <button className="btn-close" onClick={onClose}>✕</button>
          </div>

          <form onSubmit={handleSubmit} className="cliente-form">
          
          {/* Sección de cámara para cédula */}
          <div className="form-section">
            <h3>📸 Escanear Cédula con IA</h3>
            <button
              type="button"
              className="btn-camera"
              onClick={iniciarCamara}
              disabled={showCamera || ocrProcessing}
            >
              {showCamera ? 'Cámara Activa' : ocrProcessing ? 'Procesando...' : '📷 Abrir Cámara'}
            </button>

            {showCamera && (
              <div className="camera-container">
                {/* Marco guía de cédula */}
                <div className="cedula-guide">
                  <div className={`cedula-frame ${cedulaDetectada ? 'detectada' : ''}`}></div>
                  <p className="guide-text">
                    {cedulaDetectada 
                      ? '✓ Cédula detectada - Capturando...' 
                      : 'Coloca la cédula dentro del marco'}
                  </p>
                </div>
                
                {/* Countdown para captura automática */}
                {countdown > 0 && (
                  <div className="countdown-overlay">
                    {countdown}
                  </div>
                )}
                
                <video ref={videoRef} autoPlay playsInline muted className="camera-video"></video>
                <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                <div className="camera-controls">
                  <button type="button" className="btn-capture" onClick={capturarFoto}>
                    📸 Capturar Ahora
                  </button>
                  <button type="button" className="btn-cancel-camera" onClick={cancelarCamara}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {ocrProcessing && (
              <div className="ocr-processing">
                <p>🤖 Procesando imagen con IA...</p>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${ocrProgress}%` }}></div>
                </div>
                <p>{ocrProgress}%</p>
              </div>
            )}

            {formData.imagenCedula && (
              <div className="preview-image">
                <img src={formData.imagenCedula} alt="Cédula capturada" />
                <p>✓ Imagen capturada {formData.datosExtraIdosIA?.procesado && '- Datos extraídos con IA'}</p>
              </div>
            )}
          </div>

          {/* Cédula y Nombre */}
          <div className="form-section">
            <h3>👤 Datos Personales</h3>
            <div className="form-group">
              <label>Cédula *</label>
              <input
                type="text"
                name="cedula"
                value={formData.cedula}
                onChange={handleChange}
                placeholder="Ej: 001-1234567-8"
                required
              />
            </div>

            <div className="form-group">
              <label>Nombre Completo * (MAYÚSCULAS)</label>
              <input
                type="text"
                name="nombreCompleto"
                value={formData.nombreCompleto}
                onChange={handleChange}
                placeholder="JUAN PÉREZ GARCÍA"
                required
              />
            </div>
          </div>

          {/* Dirección y GPS */}
          <div className="form-section">
            <h3>📍 Ubicación</h3>
            <div className="form-group">
              <label>Dirección Principal *</label>
              <textarea
                name="direccionPrincipal"
                value={formData.direccionPrincipal}
                onChange={handleChange}
                placeholder="Calle, número, sector, ciudad"
                required
              />
            </div>

            <div className="form-group">
              <button
                type="button"
                className="btn-gps"
                onClick={obtenerUbicacionGPS}
                disabled={gpsLoading}
              >
                {gpsLoading ? '⏳ Obteniendo GPS...' : '📡 Obtener Ubicación GPS'}
              </button>
              {gpsStatus && <p className="gps-status">{gpsStatus}</p>}
              {formData.coordenadasGPS.latitud && (
                <div className="gps-info">
                  <p><strong>Latitud:</strong> {formData.coordenadasGPS.latitud.toFixed(6)}</p>
                  <p><strong>Longitud:</strong> {formData.coordenadasGPS.longitud.toFixed(6)}</p>
                  <p><strong>Precisión:</strong> {formData.coordenadasGPS.precision?.toFixed(1)} metros</p>
                  <button
                    type="button"
                    className="btn-show-map"
                    onClick={() => {
                      setTempCoords(formData.coordenadasGPS);
                      setShowMapModal(true);
                    }}
                  >
                    🗺️ Ver/Ajustar en Mapa
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Datos de servicio */}
          <div className="form-section">
            <h3>📶 Datos de Servicio</h3>
            <div className="form-group">
              <label>Número Caja NAT * (Debe comenzar con FN-)</label>
              <input
                type="text"
                name="numeroCajaNAT"
                value={formData.numeroCajaNAT}
                onChange={handleChange}
                placeholder="FN-12345"
                required
              />
            </div>

            <div className="form-group">
              <label>Teléfono WhatsApp * (809, 829 o 849)</label>
              <input
                type="tel"
                name="telefonoWhatsApp"
                value={formData.telefonoWhatsApp}
                onChange={handleChange}
                placeholder="8091234567"
                required
              />
            </div>
          </div>

          {/* Datos WiFi */}
          <div className="form-section">
            <h3>📡 Configuración WiFi</h3>
            <div className="form-group">
              <label>Nombre SSID WiFi * (Debe comenzar con FN-)</label>
              <input
                type="text"
                name="nombreSSIDWifi"
                value={formData.nombreSSIDWifi}
                onChange={handleChange}
                placeholder="FN-12345"
                required
              />
            </div>

            <div className="form-group">
              <label>Contraseña WiFi * (Mínimo 8 caracteres)</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="contrasenaWifi"
                  value={formData.contrasenaWifi}
                  onChange={handleChange}
                  placeholder="Mínimo 8 caracteres"
                  minLength="8"
                  required
                />
                <button
                  type="button"
                  className="btn-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading || ocrProcessing}
            >
              {loading ? 'Registrando...' : '✓ Registrar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* Modal de Mapa GPS */}
    {showMapModal && tempCoords.latitud && (
      <div className="map-modal-overlay" onClick={cancelarMapa}>
        <div className="map-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="map-modal-header">
            <h3>📍 Ajustar Ubicación GPS</h3>
            <button className="btn-close" onClick={cancelarMapa}>✕</button>
          </div>
          
          <div className="map-modal-content">
            <p className="map-modal-instructions">
              🔵 Arrastra el marcador o haz clic en el mapa para ajustar la ubicación exacta
            </p>
            
            <MapContainer
              center={[tempCoords.latitud, tempCoords.longitud]}
              zoom={18}
              style={{ height: '500px', width: '100%', borderRadius: '8px' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <DraggableMarker />
            </MapContainer>
            
            <div className="map-modal-coords">
              <p>📍 <strong>Coordenadas:</strong></p>
              <p>Latitud: {tempCoords.latitud.toFixed(6)}</p>
              <p>Longitud: {tempCoords.longitud.toFixed(6)}</p>
              <p>Precisión: {tempCoords.precision?.toFixed(1)} metros</p>
            </div>
          </div>
          
          <div className="map-modal-buttons">
            <button className="btn-map-cancel" onClick={cancelarMapa}>
              Cancelar
            </button>
            <button className="btn-map-accept" onClick={aceptarUbicacion}>
              ✓ Aceptar Ubicación
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default ClienteRegistro;
